// Reconciliação de DATAS financeiras: Curso → Plano → Snapshot → Títulos (nunca duplica parcela)
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, CalendarCheck, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const brDate = (s) => (String(s || '').match(/^\d{4}-\d{2}-\d{2}/)
  ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '—');

export default function ReconciliarDatasDialog({ open, onOpenChange, onDone }) {
  const [year, setYear] = useState('');   // vazio = todos os anos com plano (2026, 2027, 2028…)
  const [studentId, setStudentId] = useState('');
  const [turmas, setTurmas] = useState([]);
  const [turma, setTurma] = useState('');
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState(null);
  const [applied, setApplied] = useState(null);

  const erro = (e, fb) => toast.error(e?.response?.data?.detail || fb);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get('/class-groups');
        const list = Array.isArray(data) ? data : (data.items || []);
        setTurmas(list);
      } catch { setTurmas([]); }
    })();
  }, [open]);

  const rodarAuditoria = async () => {
    setBusy(true); setApplied(null);
    try {
      const { data } = await api.post('/financial/datas/auditoria',
        { year: year ? Number(year) : null, student_id: studentId || null, class_group_id: turma || null, limit: 500 });
      setAudit(data);
      toast.success(`${data.students_with_divergence} aluno(s) com divergência de data.`);
    } catch (e) { erro(e, 'Não foi possível auditar as datas.'); } finally { setBusy(false); }
  };

  const reconciliar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/financial/datas/reconciliar',
        { year: year ? Number(year) : null, student_id: studentId || null, class_group_id: turma || null, dry_run: false, limit: 500 });
      setApplied(data);
      toast.success(`${data.fixed_count} título(s) corrigido(s) · ${data.flagged_count} marcado(s) para revisão.`);
      onDone?.(data);
      await rodarAuditoria();
    } catch (e) { erro(e, 'Não foi possível reconciliar as datas.'); } finally { setBusy(false); }
  };

  const reports = audit?.reports || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1.5rem)] sm:w-full max-h-[92vh] overflow-y-auto"
        data-testid="reconciliar-datas-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-violet-600" /> Reconciliar datas financeiras
          </DialogTitle>
          <DialogDescription>
            Compara as datas configuradas no plano do curso (snapshot do aluno) com os títulos gerados.
            Título em aberto é corrigido no lugar; título pago fica marcado para revisão.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-3 gap-2">
          <div className="sm:col-span-3">
            <label className="text-xs font-semibold">Turma (reconciliar a turma inteira em 1 clique)</label>
            <select value={turma} onChange={(e) => setTurma(e.target.value)} data-testid="rec-class"
              className="w-full h-9 rounded-md border px-2 text-sm bg-white">
              <option value="">Todas as turmas</option>
              {turmas.map((t) => (
                <option key={t.id} value={t.id}>{t.name || t.nome} {t.year ? `· ${t.year}` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold">Ano letivo (vazio = todos)</label>
            <Input type="number" placeholder="Todos os anos" value={year}
              onChange={(e) => setYear(e.target.value)} data-testid="rec-year" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold">Aluno (opcional)</label>
            <Input placeholder="ID do aluno — vazio audita todos" value={studentId}
              onChange={(e) => setStudentId(e.target.value)} data-testid="rec-student" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={rodarAuditoria} disabled={busy} data-testid="rec-audit-btn">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Auditar (somente leitura)
          </Button>
          <Button onClick={reconciliar} disabled={busy || !audit?.fixable_total}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="rec-apply-btn">
            <ShieldCheck className="w-4 h-4 mr-2" /> Corrigir {audit?.fixable_total || 0} título(s) em aberto
          </Button>
        </div>

        {!!audit && (
          <div className="rounded-lg border bg-slate-50 p-3 text-sm" data-testid="rec-summary">
            <p>Anos auditados: <b>{(audit.years || []).join(' · ') || '—'}</b></p>
            <p>Alunos verificados: <b>{audit.students_checked}</b> · com divergência: <b>{audit.students_with_divergence}</b></p>
            <p>Corrigíveis (em aberto): <b className="text-emerald-700">{audit.fixable_total}</b> ·
              {' '}Pagos (revisão): <b className="text-amber-700">{audit.locked_total}</b></p>
            {!!applied && (
              <p className="text-[12px] text-slate-600 mt-1">
                Última execução: {applied.fixed_count} corrigido(s) · {applied.flagged_count} marcado(s)
                {applied.contracts_refreshed ? ` · ${applied.contracts_refreshed} contrato(s) não assinado(s) atualizado(s)` : ''}
              </p>
            )}
          </div>
        )}

        {reports.map((r) => (
          <div key={`${r.student_id}-${r.year}`} className="rounded-lg border" data-testid={`rec-report-${r.student_id}`}>
            <p className="px-3 py-2 bg-slate-50 border-b text-sm font-semibold">
              {r.student_name || r.student_id} <span className="text-slate-500 font-normal">· {r.year}</span>
            </p>
            <div className="divide-y text-[12.5px]">
              {r.divergences.map((d) => (
                <div key={d.title_id} className="px-3 py-2 flex flex-wrap items-center gap-2">
                  <span className="flex-1 truncate">{d.description} · parcela {d.installment_number}/{d.total_installments}</span>
                  <span className="text-slate-500">{brDate(d.current_due_date)} → <b className="text-slate-800">{brDate(d.expected_due_date)}</b></span>
                  {d.fixable ? (
                    <span className="text-[11px] text-emerald-700 font-semibold">corrigível</span>
                  ) : (
                    <span className="text-[11px] text-amber-700 font-semibold flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> divergência histórica
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
